import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { initializeAuth, getCurrentUser, registerWithFamily } from '../services/apiService';
import { storageService } from '../services/storageService';
import { useAppContext } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';
import { authCardClass, authInputClass, authLabelClass, authScreenShellClass } from './authUiClasses';
import { i18n } from '../i18n/i18n';

/**
 * PKCE OAuth returns ?code= in the URL. The client must exchange it for a session;
 * getSession() alone does not do that. Dedupe with sessionStorage for React Strict Mode.
 */
async function exchangePkceCodeIfPresent(): Promise<{ error: Error | null }> {
  if (typeof window === 'undefined') return { error: null };
  const href = window.location.href;
  const url = new URL(href);
  const code = url.searchParams.get('code');
  if (!code) return { error: null };

  const seenKey = `ftm_oauth_pkce_${code}`;
  if (sessionStorage.getItem(seenKey) === '1') {
    return { error: null };
  }

  const { error } = await supabase.auth.exchangeCodeForSession(href);
  if (error) return { error: new Error(error.message) };

  sessionStorage.setItem(seenKey, '1');
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  const qs = url.searchParams.toString();
  window.history.replaceState({}, '', url.pathname + (qs ? `?${qs}` : '') + url.hash);
  return { error: null };
}

const OAuthCallback: React.FC = () => {
  const [params] = useSearchParams();
  const [error, setError] = useState<string>('');
  const [pending, setPending] = useState<boolean>(true);
  const [signupData, setSignupData] = useState<{ email?: string | null; display_name?: string | null } | null>(null);
  const [familyName, setFamilyName] = useState(() => i18n.t('auth.family_default'));
  const [name, setName] = useState('');
  const [betaCode, setBetaCode] = useState('');
  const [formErr, setFormErr] = useState('');
  const navigate = useNavigate();
  const { refreshFamilyData, refreshUserData, bindDevice, refreshDeviceSession } = useAppContext() as any;

  // Supabase OAuth (Google/Facebook) is the only sign-in path; the old server-ticket flow is gone.
  useEffect(() => {
    handleSupabaseCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSupabaseCallback = async () => {
    try {
      const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
      const oauthErr = url?.searchParams.get('error_description') || url?.searchParams.get('error');
      if (oauthErr) {
        setError(decodeURIComponent(oauthErr.replace(/\+/g, ' ')));
        setPending(false);
        return;
      }

      const pkceErr = await exchangePkceCodeIfPresent();
      if (pkceErr.error) {
        setError(pkceErr.error.message);
        setPending(false);
        return;
      }

      let activeSession = (await supabase.auth.getSession()).data.session;
      if (!activeSession) {
        await new Promise((r) => setTimeout(r, 500));
        activeSession = (await supabase.auth.getSession()).data.session;
      }
      if (!activeSession) {
        setError(i18n.t('auth.oauth_failed'));
        setPending(false);
        return;
      }

      initializeAuth(activeSession.access_token);
      storageService.setAuthToken(activeSession.access_token);

      // Check if user has a family
      const user = await getCurrentUser();
      if (user.family_id) {
        await refreshUserData();
        await refreshFamilyData();
        try { await bindDevice('Web Browser'); await refreshDeviceSession(); } catch {}
        // Coming back from UserProfile's "Link to Google/Facebook" (an existing, already-signed-in
        // guardian linking an extra identity) rather than a fresh login — send them back to
        // Profile with a success message instead of Home.
        const linkIntentProvider = params.get('intent') === 'link' ? params.get('provider') : null;
        navigate(linkIntentProvider ? `/profile?linked=${linkIntentProvider}` : '/home', { replace: true });
      } else {
        // New OAuth user without a family — show signup form
        setSignupData({
          email: activeSession.user.email,
          display_name: activeSession.user.user_metadata?.full_name || activeSession.user.user_metadata?.name || '',
        });
        setName(activeSession.user.user_metadata?.full_name || activeSession.user.user_metadata?.name || '');
        setPending(false);
      }
    } catch (e: any) {
      setError(e?.message || i18n.t('auth.oauth_failed'));
      setPending(false);
    }
  };

  const completeSignup = async () => {
    setFormErr('');
    if (!betaCode.trim()) {
      setFormErr(i18n.t('auth.err_code_required'));
      return;
    }
    try {
      await registerWithFamily(name || 'New User', signupData?.email || '', '', familyName, undefined, betaCode.trim());
      await refreshUserData();
      await refreshFamilyData();
      try { await bindDevice('Web Browser'); await refreshDeviceSession(); } catch {}
      navigate('/home', { replace: true });
    } catch (e: any) {
      setFormErr(e?.message || i18n.t('auth.err_signup_failed'));
    }
  };

  if (pending) {
    return (
      <div className={`${authScreenShellClass} text-gray-900 dark:text-gray-100`}>
        <p className="text-center text-lg">{i18n.t('auth.processing')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={authScreenShellClass}>
        <div className={authCardClass}>
          <p className="text-center text-red-600 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  if (signupData) {
    return (
      <div className={authScreenShellClass}>
        <div className={authCardClass}>
          <h2 className="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100">{i18n.t('auth.complete_setup')}</h2>
          {formErr && <div className="text-sm text-red-600 dark:text-red-300 mb-3">{formErr}</div>}
          <div className="mb-3">
            <label className={authLabelClass}>{i18n.t('auth.beta_code')}</label>
            <input className={authInputClass} placeholder={i18n.t('auth.beta_code_ph')} value={betaCode} onChange={(e) => setBetaCode(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className={authLabelClass}>{i18n.t('auth.family_name')}</label>
            <input className={authInputClass} placeholder={i18n.t('auth.family_name_ph')} value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className={authLabelClass}>{i18n.t('auth.your_name')}</label>
            <input className={authInputClass} placeholder={i18n.t('auth.full_name_ph')} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <button className="px-4 py-2 rounded text-btn-text-primary bg-btn-success hover:bg-btn-success-hover" onClick={completeSignup}>{i18n.t('auth.complete_setup_btn')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${authScreenShellClass} text-gray-600 dark:text-gray-400`}>
      <p className="text-center">{i18n.t('auth.processing')}</p>
    </div>
  );
};

export default OAuthCallback;


